import { GetServerSidePropsContext } from 'next';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import SEO from '@components/common/SEO/SEO';
import { PlatformType } from '@invideoio/web-shared/constants/common';
import ToolLandingLayout from '@layouts/LandingPageLayout/ToolLandingLayout';
import { getToolsRootPage } from '@lib/API';
import { SET_OS, SET_PLATFORM } from '@store/device-state';
import { useAppSelector } from '@store/hook';
import { getUploadFileDetails } from '@store/upload-state/upload-state';
import { userStateSelector } from '@store/user-state/UserState';
import { generateJSONLDSchema } from '@utils/seo';
import { RootProps } from 'src/pages/_app';
import { ToolsRootResponseType } from 'src/types';
import ToolRootPageContainer from '@layouts/LandingPageLayout/ToolRootPageContainer';

interface ToolProps extends RootProps {
  toolsPageState: ToolsRootResponseType;
}

function ToolRootPage(props: ToolProps) {
  const { toolsPageState, platform, os, browser, socialLinks } = props;

  const { SEO: SEOSection } = toolsPageState;

  const dispatch = useDispatch();
  const router = useRouter();

  const user = useAppSelector(userStateSelector);
  const uploadState = useAppSelector(getUploadFileDetails);

  const LDschema = generateJSONLDSchema(
    {
      Title: SEOSection.Title,
      Description: SEOSection.Description,
    },
    SEOSection.CanonicalURL,
    'WebPage',
  );

  useEffect(() => {
    if (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) {
      dispatch(SET_PLATFORM(PlatformType.Tablet));
    } else {
      dispatch(SET_PLATFORM(platform));
    }
    dispatch(SET_OS(os));

    // Cancel the upload if user navigates backs to LP
    if (uploadState.cancelToken) {
      uploadState.cancelToken('Cancelled Previous upload');
    }
  }, []);

  return (
    <ToolLandingLayout>
      <SEO
        title={SEOSection.Title}
        description={SEOSection.Description}
        canonicalurl={SEOSection.CanonicalURL}
        keywords={SEOSection.Keywords}
        robotIndex={SEOSection.RobotIndex}
        robotFollow={SEOSection.RobotFollow}
      >
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: `${JSON.stringify(LDschema)}`,
          }}
        />
      </SEO>
      <ToolRootPageContainer
        rootPageState={toolsPageState}
        user={user}
        socialLinks={socialLinks}
        browser={browser}
        platform={platform}
      />
    </ToolLandingLayout>
  );
}

export async function getServerSideProps(ctx: GetServerSidePropsContext) {
  let toolsPageState = null;
  const lang = ctx.locale;

  try {
    toolsPageState = await getToolsRootPage(lang);
  } catch (error) {
    console.error(error);
    return {
      notFound: true,
    };
  }
  return { props: { toolsPageState } };
}

export default ToolRootPage;
